import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { webappURL, uploadUrl, signUpUrl, sellUrl, feesUrl, loginUrl, brandUrl } from '../../util/url';
import { useAuthStore } from '../../store/useAuthStore';
import { useUploadGuard } from '../../hook/useUploadGuard';

import './Sell.css';

function UsersIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-7 7a7 7 0 0 1 14 0v1H5Z" />
    </svg>
  );
}

function ShieldIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2l8 3v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V5l8-3z" />
      <path d="M10 12l2 2 4-4" stroke="#fff" strokeWidth={2} fill="none" />
    </svg>
  );
}

function SparkIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
  </svg>
  );
}

const HERO_BANNER = "https://image.production.fruitsfamily.com/public/collection/resized%40width620/Nbr7v29QZ-img_sell_header.png";
const APP_MOCKUP = "https://image.production.fruitsfamily.com/public/collection/resized%40width1125/qWMV6HhE6-Group%207%283%29.png";

const topBrands = [
  "Japanese Vintage",
  "Vintage",
  "Vivienne Westwood",
  "Stussy",
  "Polo Ralph Lauren",
  "Bape",
  "KAPITAL",
  "Comme des Garcons",
  "Coyseio",
  "Maison Margiela",
  "Yohji Yamamoto",
  "Supreme",
  "Balenciaga",
  "ARCTERYX",
  "Hysteric Glamour",
  "American Vintage",
  "Prada",
  "Carhartt",
  "Oakley",
  "Nike",
  "Adidas",
  "XLIM",
  "Hatchingroom",
  "Diesel",
  "RRL",
  "SANSAN GEAR",
  "Patagonia",
  "Dickies",
  "Issey Miyake",
  "Chrome Hearts",
];

function Stat({ statisticValue, statisticLabel, description }) {
  return (
    <div className="Sell-stat-card">
      <div className="Sell-stat-k">{statisticValue}</div>
      <div className="Sell-stat-v">{statisticLabel}</div>
      <p className="Sell-stat-d">{description}</p>
    </div>
  );
}

Stat.propTypes = {
  statisticValue: PropTypes.node.isRequired,
  statisticLabel: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

function Step({ no, title, desc }) {
  return (
    <div className="Sell-step">
      <div className="Sell-step-no">{no}</div>
      <h3 className="Sell-step-title">{title}</h3>
      <p className="Sell-step-desc">{desc}</p>
    </div>
  );
}

Step.propTypes = {
  no: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
};

function Benefit({ icon, title, desc }) {
  return (
    <div className="Sell-benefit-card">
      <div className="Sell-benefit-header">
        <span className="Sell-benefit-icon">{icon}</span>
        <h3 className="Sell-benefit-title">{title}</h3>
      </div>
      <div className="Sell-benefit-desc">{desc}</div>
    </div>
  );
}

Benefit.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  desc: PropTypes.node.isRequired,
};

function Faq({ question, answer }) {
  return (
    <div className="Sell-faq-item is-open">
      <div className="Sell-faq-summary">
        <div className="Sell-faq-summary-inner">
          <h3 className="Sell-faq-q">{question}</h3>
        </div>
      </div>
      <p className="Sell-faq-a">{answer}</p>
    </div>
  );
}

Faq.propTypes = {
  question: PropTypes.string.isRequired,
  answer: PropTypes.string.isRequired,
};

function SoldProductCard({ brand, name, price, img, soldAt }) {
  return (
    <div className="Sell-sold-card">
      <img className="Sell-sold-img" src={img} alt={`${brand} ${name}`} />
      <div className="Sell-sold-info">
        <div className="Sell-sold-brand">{brand}</div>
        <div className="Sell-sold-name">{name}</div>
        <div className="Sell-sold-price">{price}</div>
        <div className="Sell-sold-time">{soldAt.fromNow()}</div>
      </div>
    </div>
  );
}

SoldProductCard.propTypes = {
  brand: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  img: PropTypes.string.isRequired,
  soldAt: PropTypes.object.isRequired,
};

export default function Sell({ hostname, userAgent }) {
  const navigate = useNavigate();
  const { isLogin } = useAuthStore();
  const { guardUploadClick } = useUploadGuard(userAgent);

  const handleUploadClick = (e) =>
    guardUploadClick(e, {
      onAllowed: () => {
        if (!isLogin) {
          navigate(loginUrl());
          return;
        }
        navigate(uploadUrl());
      },
    });
  const baseUrl = webappURL(hostname);
  const canonical = baseUrl + sellUrl();

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '홈', item: baseUrl + '/' },
      { '@type': 'ListItem', position: 2, name: '판매하기', item: canonical },
    ],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '수수료는 정말 0원인가요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '구매자 부담을 선택하면 판매 수수료는 0원이며 판매가 100%가 정산됩니다. 판매자 부담도 선택 가능하며 상세 비율은 수수료 안내에서 확인하세요.',
        },
      },
      {
        '@type': 'Question',
        name: '정산은 언제 되나요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '구매자가 수령을 확정하면 정산이 시작됩니다. 은행 영업일 기준으로 빠르게 입금 처리됩니다.',
        },
      },
      {
        '@type': 'Question',
        name: '직거래/계좌이체로도 가능한가요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '가능하더라도 권장하지 않습니다. 안전결제를 이용해야 사기 방지와 구매자/판매자 보호가 적용됩니다.',
        },
      },
      {
        '@type': 'Question',
        name: '프로 판매자 전용 기능이 있나요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '있습니다. 재고/송장 관리 등 전문 기능을 제공하며, 별도 입점 절차가 필요합니다.',
        },
      },
    ],
  };

  return (
    <div className="Sell container">
      <Helmet>
        <title>후루츠패밀리에서 판매하기 | 판매 수수료 0원, 정산 100%</title>
        <meta name="description" content="후루츠에서 빈티지부터 디자이너 브랜드까지 쉽고 빠르게 판매하세요. 구매자 부담 선택 시 판매 수수료 0원, 판매가는 100% 정산됩니다." />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="후루츠패밀리에서 판매하기 | 판매 수수료 0원, 정산 100%" />
        <meta property="og:description" content="후루츠에서 빈티지부터 디자이너 브랜드까지 쉽고 빠르게 판매하세요." />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="후루츠패밀리" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:image" content={HERO_BANNER} />
        <meta property="og:image:alt" content="후루츠 판매하기 - 수수료 0원, 정산 100%" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="후루츠패밀리에서 판매하기 | 판매 수수료 0원, 정산 100%" />
        <meta name="twitter:description" content="후루츠에서 빈티지부터 디자이너 브랜드까지 쉽고 빠르게 판매하세요." />
        <meta name="twitter:image" content={HERO_BANNER} />
        <link rel="preconnect" href="https://image.production.fruitsfamily.com" crossOrigin="" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      </Helmet>

      {/* Hero */}
      <section className="Sell-hero">
        <nav aria-label="Breadcrumb" className="Sell-breadcrumb">
          <ol className="Sell-breadcrumb-list">
            <li><Link to="/" className="Sell-breadcrumb-link">홈</Link></li>
            <li aria-hidden>›</li>
            <li aria-current="page" className="Sell-breadcrumb-current">판매하기</li>
          </ol>
        </nav>

        <div className="Sell-hero-grid">
          <div className="Sell-hero-left">
            <p className="Sell-hero-badge">180만 패션러버의 선택</p>
            <h1 className="Sell-hero-title">
              가치 있는 옷<br />
              <span className="Sell-hero-highlight">후루츠에서 팔자</span>
            </h1>
            <p className="Sell-hero-desc">정산은 100%, 판매 수수료는 영원히 0원.</p>
            <div className="Sell-cta-row">
              <button className="Sell-cta-primary" onClick={handleUploadClick}>지금 판매하기</button>
              <Link className="Sell-cta-secondary" to={feesUrl()}>수수료 안내 보기</Link>
            </div>
            <div className="Sell-hero-sub">
              <Link className="sr-only" to={signUpUrl()}>전문 판매자(프로 상점)이세요?</Link>
            </div>
          </div>
          <div className="Sell-hero-right">
            <img
              className="Sell-hero-img"
              src={HERO_BANNER}
              alt="후루츠 판매하기 히어로 배너"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Proof / Value */}
      <section className="Sell-stats">
        <div className="Sell-stats-grid">
          <Stat statisticValue="+180만 명" statisticLabel="패션 커뮤니티" description="패션을 사랑하는 사람들 180만명 이상이 후루츠에서 연결됩니다." />
          <Stat statisticValue="0원" statisticLabel="판매 수수료" description="판매자가 선택하는 수수료 정책. 구매자 부담을 선택하면 판매가 100% 정산됩니다." />
          <Stat 
            statisticValue={<span className="Sell-stat-star">★ 4.9</span>} 
            statisticLabel="앱스토어 평점" 
            description="3.2만+ 리뷰. 판매자부터 구매자까지 만족하는 후루츠패밀리 유저들." 
          />
        </div>
      </section>

      {/* Real-time Sold Products */}
      {/* <section className="Sell-sold-section">
        <div className="Sell-container">
          <h2 className="Sell-section-title">방금 판매된 상품들</h2>
          <p className="Sell-section-sub">지금 막 거래된 다양한 아이템을 확인해보세요</p>
          <div className="Sell-marquee">
            <div className="Sell-marquee-track animate-scroll-left" style={{ animationDuration: '60s' }}>
              {[...soldItems, ...soldItems].map((item, index) => (
                <SoldProductCard key={index} {...item} />
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Brand Spectrum */}
      <section className="Sell-brand-section">
        <div className="Sell-container">
          <h2 className="Sell-section-title">패션 중고거래는 역시 후루츠</h2>
          <p className="Sell-section-sub">
            빈티지부터 국내, 해외, 디자이너 브랜드까지.<br />
            후루츠에선 다양한 취향의 브랜드가 잘 팔려요.
          </p>

          <div className="Sell-spectrum">
            <div className="Sell-spectrum-bg"></div>
            <div className="Sell-spectrum-labels">
              {["빈티지", "국내 브랜드", "해외 브랜드", "명품"].map((label) => (
                <div key={label} className="Sell-spectrum-label">{label}</div>
              ))}
            </div>
          </div>

          <div className="Sell-brands">
            <h3 className="Sell-brands-title">지금 후루츠에서 잘 팔리는 브랜드 TOP 30</h3>
            <div className="Sell-brands-grid">
              {topBrands.map((brand, index) => (
                <Link key={index} className="Sell-brand-item" to={brandUrl(brand)}>
                  {index + 1}. {brand}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Sellers Choose Fruits */}
      <section className="Sell-benefits-section">
        <div className="Sell-container">
          <h2 className="Sell-section-title">왜 후루츠에서 잘 팔리나요?</h2>
          <p className="Sell-section-sub">노출은 높게, 신뢰는 탄탄하게, 운영은 간편하게</p>
          <div className="Sell-benefits">
            <Benefit 
              icon={<UsersIcon />} 
              title="패션 찐이 가장 많이 모인 곳" 
              desc={
                <ul>
                  <li>가장 패션에 진심인 유저들이 모인 커뮤니티</li>
                  <li>서로의 취향을 존중하고 상품의 가치를 알아줘요</li>
                </ul>
              } 
            />
            <Benefit 
              icon={<SparkIcon />} 
              title="패션에 최적화된 노출" 
              desc={
                <ul>
                  <li>감도 높은 UX로 브랜드, 사이즈까지 정밀 매칭</li>
                  <li>상품이 딱 맞는 구매자에게 잘 보이도록 설계</li>
                </ul>
              } 
            />
            <Benefit 
              icon={<ShieldIcon />} 
              title="믿고 거래하는 후루츠" 
              desc={
                <ul>
                  <li>구매자가 가장 신뢰하는 패션 중고 플랫폼</li>
                  <li>강한 신뢰로 판매 전환율이 높아요</li>
                </ul>
              } 
            />
          </div>
        </div>
      </section>

      {/* How to Sell */}
      <section className="Sell-how-section">
        <div className="Sell-container">
          <div className="Sell-how-grid">
            <div className="Sell-how-left">
              <h2 className="Sell-section-title">판매는 쉽고 빨라요</h2>
              <p className="Sell-section-sub">누구나 쉽게 올리고, 빠르게 팔 수 있어요</p>
              <div className="Sell-steps">
                <Step no="1" title="사진 찍고 등록" desc="판매할 상품을 촬영하고, 브랜드/상태/가격을 입력해 올려주세요." />
                <Step no="2" title="채팅 없이 거래" desc="구매자가 결제하면 자동으로 판매가 성사되고 택배로 보내면 끝!" />
                <Step no="3" title="정산받기" desc="구매자가 수령확정하면 빠르게 정산해드려요. 은행 영업일 기준 1~2일 소요." />
              </div>
            </div>
            <div className="Sell-how-right">
              <img className="Sell-how-img" src={APP_MOCKUP} alt="후루츠 앱 UI" />
            </div>
          </div>
        </div>
      </section>

      {/* Callout */}
      <section className="Sell-callout">
        <div className="Sell-container">
          <div className="Sell-callout-box">
            <h3 className="Sell-callout-title">
              거래 수수료, <span>직접 선택하세요</span>
            </h3>
            <p className="Sell-callout-desc">
              구매자 부담을 선택하면 판매 수수료는 0원, 판매가는 100% 정산됩니다.
            </p>
            <div className="Sell-cta-row-center">
              <Link className="Sell-cta-primary" to={feesUrl()}>수수료 구조 보기</Link>
              <button className="Sell-cta-secondary" onClick={handleUploadClick}>지금 등록하고 판매하기</button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="Sell-faq-section">
        <div className="Sell-container">
          <h2 className="Sell-section-title">자주 묻는 질문</h2>
          <div className="Sell-faq-grid">
            <Faq question="수수료는 정말 0원인가요?" answer="구매자 부담을 선택하면 판매 수수료는 0원이며 판매가 100%가 정산됩니다. 판매자 부담도 선택 가능하며 상세 비율은 수수료 안내에서 확인하세요." />
            <Faq question="정산은 언제 되나요?" answer="구매자가 수령을 확정하면 정산이 시작됩니다. 은행 영업일 기준으로 빠르게 입금 처리됩니다." />
            <Faq question="직거래/계좌이체로도 가능한가요?" answer="가능하더라도 권장하지 않습니다. 안전결제를 이용해야 사기 방지와 구매자/판매자 보호가 적용됩니다." />
            <Faq question="프로 판매자 전용 기능이 있나요?" answer="있습니다. 재고/송장 관리 등 전문 기능을 제공하며, 별도 입점 절차가 필요합니다." />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <footer className="Sell-footer">
        <div className="Sell-container">
          <h2 className="Sell-footer-title">지금 바로 판매를 시작해보세요</h2>
          <p className="Sell-footer-desc">쉽게 빠르게 판매. 결제는 안전하게. 정산은 100%.</p>
          <div className="Sell-cta-row-center">
            <button className="Sell-cta-primary" onClick={handleUploadClick}>판매 시작하기</button>
            <Link className="Sell-cta-secondary sr-only" to={signUpUrl()}>전문 판매자 등록</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

Sell.propTypes = {
  hostname: PropTypes.string.isRequired,
  userAgent: PropTypes.string.isRequired,
};
